import type { StageStripRow } from '@/components/order/ProductionTimeline'
import type { DepartmentDeadline, Order } from '@/types'

/**
 * Rows for the team and customer order pages. Done comes from the section's own
 * complete or skip line, the rule the public tracker uses, not from where the
 * job pointer sits: a job queued between sections used to show the sections it
 * had finished as Planned. A queued job is not lit up as in progress; the
 * section it waits for reads Next.
 *
 * `team` switches on the console wording: sections to come read Due, and an
 * open section past its day reads "Late, due" in red. Customers keep Planned
 * and never see lateness - their promise is the ready date, as on the tracker.
 */
export function stageRowsFor(
  deadlines: DepartmentDeadline[],
  order: Pick<Order, 'status' | 'job'>,
  { team = false }: { team?: boolean } = {},
): StageStripRow[] {
  const logs = order.job?.stage_logs ?? []
  const finishedAt = (department: string) =>
    [...logs].reverse().find((l) => l.department === department && (l.action === 'complete' || l.action === 'skip'))?.at
  return deadlines.map((d) => {
    const finished = finishedAt(d.department)
    const done = order.status === 'completed' || !!d.completed_at || !!finished
    const atThis = !done && order.job?.current_department?.id === d.department_id
    const working = atThis && order.job?.status !== 'queued'
    return {
      key: d.department_id,
      department: d.department,
      date: done ? (d.completed_at ?? finished ?? d.date) : d.date,
      state: done ? 'done' : working ? 'current' : 'pending',
      next: atThis && !working,
      due: team,
      late: team && !done && order.status !== 'cancelled' && !!d.late,
    }
  })
}
